import {
    TyrePageState,
    ReducerActionNames,
    TyrePageErrors,
    TyreProcessStages,
    TyreRepairRequest
} from './TyreRepairRequestTypes';
import {
    AddressType,
    DealerAddressType,
    UploadedDocument,
    UploadedImage
} from '../../common/CommonTypes';
import { UploadImageTypes } from '../../../Constants/Common';
import { SelectInputHelper } from '../../common/selectInput/SelectInput';

//Please add additional types with | 'your type'
type Action =
    | { type: 'update-tyre-errors'; tyreErrors: TyrePageErrors }
    | { type: 'update-mileage'; mileage: string | number }
    | { type: 'update-repair-option'; repairOption: string }
    | { type: 'update-incident-date'; dateOfIncident: string }
    | { type: 'update-uploaded-images'; uploadedImages: UploadedImage[] }
    | { type: 'update-invoice'; invoice: UploadedDocument }
    | {
          type: 'update-tyre-repair-request';
          key: keyof TyreRepairRequest;
          value: string;
      }
    | { type: 'start-new-tyre-request' }
    | { type: 'fetch-current-tyre-request' }
    | { type: 'update-tyre-stage'; newStage: TyreProcessStages }
    | { type: 'add-tyre-request' }
    | { type: 'set-policy-data'; address: AddressType }
    | { type: 'set-repair-address-selected'; value: number }
    | { type: 'set-dealer-address'; address: DealerAddressType }
    | { type: 'set-garage-address'; address: AddressType }
    | { type: 'set-default-dealer-address'; address: AddressType }
    | { type: 'set-has-errors'; hasErrors: boolean }
    | { type: 'set-permission-granted'; permissionGranted: boolean }
    | {
          type: 'remove-selected-tyre';
          tyre: string;
          newStage: TyreProcessStages;
      }
    | { type: 'set-tyre'; tyre: string; productReference: string }
    | { type: 'view-tyre'; tyre: string; newStage: TyreProcessStages }
    | { type: 'update-tyre'; updateTyre: string }
    | { type: 'scroll-to-address'; scrollToAddress: boolean }
    | { type: 'show-alert'; showCancelAlert: boolean }
    | {
          type: 'set-loading-request-submission';
          loadingOnRequestSubmission: boolean;
      }
    | { type: 'clear-repair-requests' };

const TyreRepairRequestReducer = (
    state: TyrePageState,
    action: Action
): TyrePageState => {
    switch (action.type) {
        case ReducerActionNames.UpdateTyreErrors:
            return {
                ...state,
                tyreFormErrors: action.tyreErrors
            };
        case ReducerActionNames.SetHasErrors:
            return {
                ...state,
                hasErrors: action.hasErrors
            };
        case ReducerActionNames.UpdateMileage:
            return {
                ...state,
                mileage: action.mileage
            };
        case ReducerActionNames.UpdateRepairOption:
            return {
                ...state,
                repairOption: action.repairOption
            };
        case ReducerActionNames.UpdateIncidentDate:
            return {
                ...state,
                dateOfIncident: action.dateOfIncident,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    dateOfIncident: action.dateOfIncident
                }
            };
        case ReducerActionNames.UpdateTyreRepairRequest: {
            const updatedCurrentRepairRequest = {
                ...state.currentRepairRequest,
                [action.key]: action.value
            };

            const updatedRepairRequests = state.tyreRepairRequests.map(
                (repairRequest, index) => {
                    if (index !== state.repairRequestSelectedIndex) {
                        return repairRequest;
                    } else {
                        return updatedCurrentRepairRequest;
                    }
                }
            );

            return {
                ...state,
                currentRepairRequest: updatedCurrentRepairRequest,
                tyreRepairRequests: updatedRepairRequests
            };
        }
        case ReducerActionNames.UpdateInvoice: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    invoice: action.invoice
                }
            };
        }
        case ReducerActionNames.UpdateUploadedImages: {
            return {
                ...state,
                currentRepairRequest: {
                    ...state.currentRepairRequest,
                    uploadedImages: action.uploadedImages
                }
            };
        }
        case ReducerActionNames.UpdateTyreStage: {
            return {
                ...state,
                tyreStage: action.newStage
            };
        }
        case ReducerActionNames.SetPolicyData: {
            return {
                ...state,
                repairAddresses: [...state.repairAddresses, action.address]
            };
        }
        case ReducerActionNames.UpdateGarageAddress: {
            return {
                ...state,
                garageAddresses: [action.address]
            };
        }
        case ReducerActionNames.UpdateRepairAddressSelected: {
            return {
                ...state,
                repairAddressSelectedIndex: action.value
            };
        }
        case ReducerActionNames.UpdateDealerAddress: {
            return {
                ...state,
                dealerAddress: action.address
            };
        }
        case ReducerActionNames.SetPermissionGranted: {
            return {
                ...state,
                permissionGranted: action.permissionGranted
            };
        }
        case ReducerActionNames.RemoveTyre: {
            const newTyreRepairRequests = state.tyreRepairRequests.filter(
                (rr) => rr.damagedTyre !== action.tyre
            );

            const repairRequestIndex = newTyreRepairRequests.length - 1;

            return {
                ...state,
                tyreRepairRequests: newTyreRepairRequests,
                repairRequestSelectedIndex: repairRequestIndex,
                currentRepairRequest: newTyreRepairRequests[repairRequestIndex],
                tyreStage: action.newStage
            };
        }
        case ReducerActionNames.SetTyre: {
            const repairRequestIndex = state.tyreRepairRequests.findIndex(
                (rr) => rr.damagedTyre === action.tyre
            );

            if (repairRequestIndex > -1) {
                if (repairRequestIndex === state.repairRequestSelectedIndex) {
                    return {
                        ...state,
                        tyreFormErrors: {}
                    };
                }

                return {
                    ...state,
                    tyreFormErrors: {},
                    repairRequestSelectedIndex: repairRequestIndex,
                    currentRepairRequest:
                        state.tyreRepairRequests[repairRequestIndex]
                };
            } else {
                return createNewRepairRequest(
                    state,
                    action.tyre,
                    action.productReference
                );
            }
        }
        case ReducerActionNames.AddTyreRequest: {
            const newSelectedTyres = state.currentRepairRequest.damagedTyre;
            const repairRequestIndex = state.tyreRepairRequests.findIndex(
                (rr) => rr.damagedTyre === newSelectedTyres
            );
            const newRepairRequest = state.currentRepairRequest;
            if (repairRequestIndex > -1) {
                return state;
            } else {
                return {
                    ...state,
                    tyreRepairRequests: [
                        ...state.tyreRepairRequests,
                        newRepairRequest
                    ],
                    currentRepairRequest: newRepairRequest
                };
            }
        }
        case ReducerActionNames.ShowCancelAlert: {
            return {
                ...state,
                showCancelAlert: action.showCancelAlert
            };
        }
        case ReducerActionNames.UpdateTyre: {
            return {
                ...state,
                updateTyre: action.updateTyre
            };
        }
        case ReducerActionNames.ScrollToAddress: {
            return {
                ...state,
                scrollToAddress: action.scrollToAddress
            };
        }
        case ReducerActionNames.LoadingOnRequestSubmission: {
            return {
                ...state,
                isLoadingOnSubmission: action.loadingOnRequestSubmission
            };
        }
        case ReducerActionNames.ClearRepairRequests: {
            return {
                ...state,
                tyreRepairRequests: []
            };
        }
        default:
            return state;
    }
};

const createNewRepairRequest = (
    state: TyrePageState,
    tyre: string,
    productReference: string
): TyrePageState => {
    const newIndex = state.tyreRepairRequests.length;

    const newRepairRequest: TyreRepairRequest = {
        id: newIndex,
        productReference: productReference,
        dateOfIncident: undefined,
        damagedTyre: tyre,
        damageType: SelectInputHelper.NullValue(),
        uploadedImages: [
            {
                type: UploadImageTypes.CloseUp,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Distance,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Mileage,
                uploadedPath: ''
            },
            {
                type: UploadImageTypes.Registration,
                uploadedPath: ''
            }
        ]
    };

    return {
        ...state,
        tyreFormErrors: {},
        repairRequestSelectedIndex: newIndex,
        currentRepairRequest: newRepairRequest
    };
};

export default TyreRepairRequestReducer;
